import { FC, Fragment, useReducer } from 'react';

import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { TelegramAuthBotFormDialog } from '../../telegramAuthBotFormDialog/TelegramAuthBotFormDialog';

import { telegramAuthBotService } from 'v2/api/telegramAuthBotService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import {
  TelegramAuthBotFormValues,
  TelegramAuthBotModel,
  TelegramAuthBotRequestBody,
} from 'v2/types/TelegramAuthBotModel';
import { handleHttpError } from 'v2/utils';

interface DomainBotsActionsColumnProps {
  bot: TelegramAuthBotModel;
  domainId: string;
  onRevalidate: VoidFunction;
}

export const DomainBotsActionsColumn: FC<DomainBotsActionsColumnProps> = ({ bot, domainId, onRevalidate }) => {
  const [isOpenEditDialog, setOpenEditDialog] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateTelegramAuthBot', bot.id],
    telegramAuthBotService.updateBot,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('Telegram auth bot has been updated successfully');
        setOpenEditDialog();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(
    ['deleteTelegramAuthBot', bot.id],
    telegramAuthBotService.deleteBot,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('Telegram auth bot has been deleted successfully');
      },
      onError: handleHttpError,
    }
  );

  const editInitialValues: TelegramAuthBotFormValues = {
    token: bot.token,
    name: bot.name ?? '',
    isActive: bot.isActive,
  };

  const handleSubmitEdit = (values: TelegramAuthBotRequestBody) => {
    triggerUpdate(values);
  };

  return (
    <Fragment>
      <Tooltip title="Edit" placement="top">
        <IconButton size="small" onClick={setOpenEditDialog}>
          <Pencil width={16} height={16} />
        </IconButton>
      </Tooltip>

      <Popconfirm message="Are you sure that you want to delete this bot?" onConfirm={triggerDelete}>
        <Tooltip title="Delete" placement="top">
          <IconButton size="small" color="error">
            <Trash2 width={16} height={16} />
          </IconButton>
        </Tooltip>
      </Popconfirm>

      <TelegramAuthBotFormDialog
        title="Edit Telegram Auth Bot"
        isOpen={isOpenEditDialog}
        onClose={setOpenEditDialog}
        domainId={domainId}
        initialValues={editInitialValues}
        isSubmitting={isMutating}
        onSubmit={handleSubmitEdit}
      />
    </Fragment>
  );
};

DomainBotsActionsColumn.displayName = 'DomainBotsActionsColumn';
